import React, { useEffect, useMemo, useState } from "react";
import { AdminPackage } from "api/admin";

type Mode = "create" | "edit";

export interface PackageFormModalProps {
  mode: Mode;
  initial?: Partial<AdminPackage>;
  onSubmit: (values: {
    name: string;
    slug?: string; // only used on create
    short_description: string;
    price_cents: number;
    currency: string; // always GBP
    expected_lock_version?: number; // only used on edit
  }) => Promise<void>;
  onClose: () => void;
}

const asInt = (v: string) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0;
};

type Preset = {
  name: string;
  slug: string;
  short_description: string;
  price_cents: number;
};

const PackageFormModal: React.FC<PackageFormModalProps> = ({
  mode,
  initial,
  onSubmit,
  onClose
}) => {
  const presets: Preset[] = useMemo(
    () => [
      {
        name: "Essential",
        slug: "essential",
        short_description:
          "Your first step to understanding your hormones. Perfect for early symptoms or anyone looking to establish a baseline.",
        price_cents: 23900
      },
      {
        name: "Advanced",
        slug: "advanced",
        short_description:
          "A more holistic view of your body in midlife. Combines hormone testing with bone health and heart/metabolic risk markers for a fuller picture of your internal balance.",
        price_cents: 35900
      },
      {
        name: "Ultimate",
        slug: "ultimate",
        short_description:
          "The most comprehensive menopause health check on the market. From stress to nutrition, this full biomarker screening helps tailor treatments, supplements, and lifestyle changes with confidence.",
        price_cents: 49900
      }
    ],
    []
  );

  const [name, setName] = useState(initial?.name ?? "");
  const [slug, setSlug] = useState(initial?.slug ?? "");
  const [shortDesc, setShortDesc] = useState(initial?.short_description ?? "");
  const [priceCents, setPriceCents] = useState<number>(initial?.price_cents ?? 0);

  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string>("");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const applyPreset = (p: Preset) => {
    setName(p.name);
    if (mode === "create") setSlug(p.slug);
    setShortDesc(p.short_description);
    setPriceCents(p.price_cents);
    setErr("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        slug: mode === "create" ? slug.trim().toLowerCase() : undefined,
        short_description: shortDesc.trim(),
        price_cents: Math.max(0, Math.round(priceCents)),
        currency: "GBP",
        expected_lock_version: mode === "edit" ? initial?.lock_version : undefined
      });
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === "create" ? "Create Package" : "Edit Package";

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30">
      <div className="w-full sm:max-w-xl bg-white rounded-t-3xl sm:rounded-3xl shadow-lg border border-slate-200">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="typo-h4-section-title text-[var(--c-link)] uppercase tracking-[0.18em]">
            <span aria-hidden="true">{">"}</span>
            <span className="inline-block w-2" aria-hidden="true" />
            <span>{title}</span>
          </h3>

          <button
            type="button"
            onClick={onClose}
            className="font-manrope font-normal uppercase tracking-[0.18em] text-[11px] text-[var(--c-link)] hover:opacity-80 transition-opacity"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {err && (
            <div className="rounded-2xl border border-rose-200/60 bg-rose-50 px-4 py-3">
              <p className="typo-p-supporting-text text-rose-700">{err}</p>
            </div>
          )}

          {/* Presets */}
          <div className="space-y-2">
            <p className="font-manrope font-normal uppercase tracking-[0.18em] text-[10px] text-slate-500">
              Presets
            </p>

            <div className="grid sm:grid-cols-3 gap-2">
              {presets.map((p) => (
                <button
                  key={p.slug}
                  type="button"
                  onClick={() => applyPreset(p)}
                  className={[
                    "rounded-2xl border px-3 py-2 text-left",
                    "hover:opacity-95 transition-opacity",
                    "bg-white"
                  ].join(" ")}
                  style={{ borderColor: "#f3dfae" }}
                >
                  <div className="font-manrope font-normal uppercase tracking-[0.18em] text-[11px] text-[var(--c-link)]">
                    {p.name}
                  </div>
                  <div className="mt-1 font-lexend text-[11px] text-slate-600">
                    £{(p.price_cents / 100).toFixed(0)}
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {/* Name */}
            <div className="sm:col-span-1">
              <label className="font-manrope font-normal uppercase tracking-[0.18em] text-[10px] text-slate-500">
                Name
              </label>
              <div className="mt-2 border-b border-black">
                <input
                  className={[
                    "w-full bg-transparent py-2",
                    "font-body text-base text-slate-900",
                    "outline-none placeholder:text-slate-400"
                  ].join(" ")}
                  placeholder="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Slug */}
            <div className="sm:col-span-1">
              <label className="font-manrope font-normal uppercase tracking-[0.18em] text-[10px] text-slate-500">
                Slug
              </label>
              <div className="mt-2 border-b border-black">
                <input
                  className={[
                    "w-full bg-transparent py-2",
                    "font-body text-base text-slate-900",
                    "outline-none placeholder:text-slate-400",
                    mode === "edit" ? "opacity-70" : ""
                  ].join(" ")}
                  placeholder="slug-example"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required={mode === "create"}
                  disabled={mode === "edit"}
                />
              </div>
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="font-manrope font-normal uppercase tracking-[0.18em] text-[10px] text-slate-500">
                Description
              </label>
              <div className="mt-2 border-b border-black">
                <textarea
                  className={[
                    "w-full bg-transparent py-2",
                    "font-body text-base text-slate-900",
                    "outline-none placeholder:text-slate-400",
                    "resize-none"
                  ].join(" ")}
                  rows={3}
                  placeholder="Short description"
                  value={shortDesc}
                  onChange={(e) => setShortDesc(e.target.value)}
                  required
                />
              </div>
            </div>

            {/* Cost */}
            <div className="sm:col-span-1">
              <label className="font-manrope font-normal uppercase tracking-[0.18em] text-[10px] text-slate-500">
                Cost (pennies)
              </label>
              <div className="mt-2 border-b border-black">
                <input
                  className={[
                    "w-full bg-transparent py-2",
                    "font-body text-base text-slate-900",
                    "outline-none placeholder:text-slate-400"
                  ].join(" ")}
                  type="number"
                  min={0}
                  placeholder="23900"
                  value={priceCents}
                  onChange={(e) => setPriceCents(asInt(e.target.value))}
                  required
                />
              </div>
              <p className="mt-2 font-lexend text-[11px] text-slate-500">
                Currency: <span className="font-semibold text-slate-700">GBP</span>
              </p>
            </div>

            {/* Summary */}
            <div className="sm:col-span-1 flex items-end">
              <div className="w-full rounded-2xl border border-black/10 bg-slate-50 px-4 py-3">
                <p className="font-lexend text-[11px] text-slate-600">
                  Display price:{" "}
                  <span className="font-semibold text-slate-800">
                    £{(Math.max(0, Math.round(priceCents)) / 100).toFixed(2)}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button
              type="submit"
              disabled={
                submitting || !name.trim() || !shortDesc.trim() || (mode === "create" && !slug.trim())
              }
              className={[
                "inline-flex items-center justify-center",
                "rounded-full bg-[#f3dfae] px-5 py-2",
                "text-[#151A3A] hover:opacity-95 transition-opacity",
                "disabled:opacity-50"
              ].join(" ")}
            >
              <span className="font-manrope font-normal uppercase tracking-[0.18em] text-[11px]">
                {submitting ? "Saving…" : "Save"}
              </span>
            </button>

            <button
              type="button"
              onClick={onClose}
              className={[
                "inline-flex items-center justify-center",
                "rounded-full border px-5 py-2",
                "hover:opacity-95 transition-opacity"
              ].join(" ")}
              style={{ borderColor: "#f3dfae" }}
            >
              <span className="font-manrope font-normal uppercase tracking-[0.18em] text-[11px] text-[var(--c-link)]">
                Cancel
              </span>
            </button>
          </div>

          {mode === "edit" && typeof initial?.lock_version === "number" && (
            <div className="font-lexend text-[11px] text-slate-500">
              Version: v{initial.lock_version}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default PackageFormModal;